// externals
import { RSAA } from 'redux-api-middleware';

// libraries
import type { IConfigObj } from '@makemydeal/dr-common-utils';
import { buildFromConfig } from '@makemydeal/dr-common-utils';
import type { SurchargeOverrides } from '@makemydeal/dr-shared-types';
import type { RefreshContractValidationState, DraftDealProduct, StateTree } from '@makemydeal/dr-dash-types';
import type { FSA, IPaymentMiddlewareMeta, FSAWithMeta, SimpleFSA, AnyFSA } from '@makemydeal/dr-browser-utils';
import type { RSAACallAction, RSAACallRequest } from '@makemydeal/dr-react-18-wrappers';
import type { AccelerateDispatch } from '@makemydeal/dr-shared-store';
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import { scopedStateSelectors } from '@makemydeal/shared-scoped-store';
import { updatePrevOfferAfterInit } from './internalPrevOfferActionCreators';
import { getDealXgId } from '../selectors/offerRedux';
import type { DashStoreThunk } from '../types/appState';

// consts/enums
import type { DASH_MENU_LIFECYCLE_INIT_START, DASH_MENU_LIFECYCLE_INIT_FINISH } from '../actionTypes/vehicleProtectionActionTypes';
import {
    FETCH_PRODUCTS_FAILURE,
    FETCH_PRODUCTS_REQUESTED,
    FETCH_PRODUCTS_SUCCESS,
    FETCH_RATES_FAILURE,
    FETCH_RATES_REQUESTED,
    FETCH_RATES_SUCCESS,
    PRODUCTS_REQUEST_OVERRIDDEN,
    PRODUCT_SELECTED,
    PRODUCT_UNSELECTED,
    PRODUCT_UPDATED,
    PRODUCT_CHANGES_PAYMENT_CALL,
    ALL_PRODUCTS_SELECTED,
    ALL_PRODUCTS_UNSELECTED,
    PRODUCT_MONTHLY_PAYMENT_UPDATED,
    PRODUCT_TAXED_UPDATED,
    ALL_PRODUCTS_MONTHLY_PAYMENT_RESET,
    FETCH_RATES_RETRY,
    FETCH_PRODUCTS_RETRY,
    VEHICLE_PROTECTION_COMPLETED,
    VEHICLE_PROTECTION_IN_PROGRESS,
    GAP_PRICE_UPDATED_BY_LENDER,
    GAP_PRICE_ACKNOWLEDGED,
    SET_MANUAL_PRODUCTS,
    RECALCULATE_PRODUCT_PAYMENTS,
    SET_REFRESH_CONTRACT_VALIDATION_STATE,
    SET_EDITABLE_PRODUCT_CODE
} from '../actionTypes/vehicleProtectionActionTypes';
import { FETCH_RATES_POLLING_DELAY_FACTORS } from '../types/vehicleProtectionTypes';
import * as configSelectors from '../selectors/config';
import * as connectionSelectors from '../selectors/connectionSelectors';
import * as vehicleProtectionSelectors from '../selectors/vehicleProtectionSelectors';

import { DASH_START_VEHICLE_PROTECTION } from '../actionTypes/navigationActionTypes';
import { PAYMENT_RECEIVED } from '../actionTypes/deskingActionTypes';

type PaymentSkipMeta = { skipPayment?: boolean };
export type VppProductSelectAction = FSAWithMeta<string, { productCode: string }, PaymentSkipMeta>;
export type VppProductUnselectedAction = FSAWithMeta<string, { productCode: string }, PaymentSkipMeta>;
export type SetManualVppProductsAction = FSAWithMeta<string, { products: DraftDealProduct[] }, PaymentSkipMeta>;

export type VppProductUpdatedAction = FSAWithMeta<string, DraftDealProduct, PaymentSkipMeta>;
export type VppProductRequestOverridenAction = FSA<string, SurchargeOverrides>;
export type PaymentUpdateAction = FSAWithMeta<typeof PRODUCT_CHANGES_PAYMENT_CALL, any, IPaymentMiddlewareMeta>;

// TODO@FedaiPaca: Check if these are safe to remove
export type MenuLifecycleInitStartAction = SimpleFSA<typeof DASH_MENU_LIFECYCLE_INIT_START>;
export type MenuLifecycleInitFinishAction = SimpleFSA<typeof DASH_MENU_LIFECYCLE_INIT_FINISH>;

export const completeVehicleProtection = () => {
    return {
        type: VEHICLE_PROTECTION_COMPLETED
    };
};

export const startVehicleProtection = () => {
    return {
        type: VEHICLE_PROTECTION_IN_PROGRESS
    };
};
export const selectVppProduct = (productCode: string, meta?: PaymentSkipMeta): VppProductSelectAction => {
    return {
        type: PRODUCT_SELECTED,
        payload: {
            productCode
        },
        meta
    };
};

export const setEditableVppProductCode = (productCategoryCode: string) => {
    return {
        type: SET_EDITABLE_PRODUCT_CODE,
        payload: {
            productCategoryCode
        }
    };
};

export const resetEditableVppProductCode = () => {
    return {
        type: SET_EDITABLE_PRODUCT_CODE,
        payload: {
            productCategoryCode: ''
        }
    };
};

export const setRefreshContractValidationState = (refreshValidationState: RefreshContractValidationState) => ({
    type: SET_REFRESH_CONTRACT_VALIDATION_STATE,
    payload: refreshValidationState
});

export const setManualVppProducts = (products: DraftDealProduct[]): SetManualVppProductsAction => {
    return {
        type: SET_MANUAL_PRODUCTS,
        payload: {
            products
        }
    };
};

export const recalculateProductPayments = () => {
    return {
        type: RECALCULATE_PRODUCT_PAYMENTS
    };
};

export const redirectToVehicleProtectionPage = () => {
    return { type: DASH_START_VEHICLE_PROTECTION };
};

export const unselectVppProduct = (productCode: string, meta?: PaymentSkipMeta): VppProductUnselectedAction => {
    return {
        type: PRODUCT_UNSELECTED,
        payload: {
            productCode
        },
        meta
    };
};

export const selectAllVppProducts = (): VppProductSelectAction => {
    return { type: ALL_PRODUCTS_SELECTED };
};

export const unselectAllVppProducts = (meta?: PaymentSkipMeta): VppProductUnselectedAction => {
    return { type: ALL_PRODUCTS_UNSELECTED, meta };
};

export const retainVppProducts =
    (retainedVpp: DraftDealProduct[]): DashStoreThunk =>
    (dispatch) => {
        dispatch(unselectAllVppProducts({ skipPayment: true }));

        const isCatalogProduct = (product: DraftDealProduct): product is DraftDealProduct & { productCode: string } =>
            !product.isManualProduct && Boolean(product.productCode);

        const keptCatalogProducts = retainedVpp.filter(isCatalogProduct);
        const keptManualProducts = retainedVpp.filter((product) => product.isManualProduct);

        keptCatalogProducts.forEach((product) => {
            dispatch(selectVppProduct(product.productCode, { skipPayment: true }));
        });

        dispatch(setManualVppProducts(keptManualProducts));
    };

export const updateVppProduct = (product: DraftDealProduct, meta?: PaymentSkipMeta): VppProductUpdatedAction => {
    return {
        type: PRODUCT_UPDATED,
        payload: product,
        meta
    };
};

export const updateVppSurchargesAttributes = (surcharges: SurchargeOverrides): VppProductRequestOverridenAction => {
    return {
        type: PRODUCTS_REQUEST_OVERRIDDEN,
        payload: surcharges
    };
};

const fetchProductsAbortControllerMap: Map<string, AbortController> = new Map();

export const fetchVehicleProtectionProducts = (state: StateTree, originalAction?: any): RSAACallAction => {
    const bffEndpointConfig = configSelectors.getServicesBff(state);
    const connectionId = connectionSelectors.getConnectionId(state);
    const dealerId = dealerSelectors.getDealerId(state);
    const surchargeOverrides = vehicleProtectionSelectors.getVppSurchargesOverrides(state);
    const scopeId = scopedStateSelectors.getCurrentScopeId(state as any);

    const targetProducts: DraftDealProduct[] = vehicleProtectionSelectors.getTargetProducts(state);

    const payload = {
        dealerId,
        connectionId,
        surchargeOverrides,
        ...(targetProducts.length > 0 && { targetProducts })
    };

    let abortController = fetchProductsAbortControllerMap.get(scopeId);
    if (abortController) {
        abortController.abort();
    }
    abortController = new AbortController();
    fetchProductsAbortControllerMap.set(scopeId, abortController);

    const vppBffUrl = buildFromConfig(bffEndpointConfig);
    const endpoint = `${vppBffUrl}/fetchVppProducts`;

    const request: RSAACallRequest = {
        endpoint,
        method: 'POST',
        body: JSON.stringify(payload),
        headers: { 'Content-Type': 'application/json' },
        options: {
            signal: abortController.signal
        },
        types: [FETCH_PRODUCTS_REQUESTED, { type: FETCH_PRODUCTS_SUCCESS, meta: { originalAction } }, FETCH_PRODUCTS_FAILURE]
    };
    return {
        [RSAA]: request
    } as RSAACallAction;
};

const fetchRatesAbortControllerMap: Map<string | undefined, AbortController> = new Map();
export const fetchVehicleProtectionRates =
    (rateToken: string, bffEndpointConfig: IConfigObj, scopeId?: string, meta?: any) =>
    (dispatch: AccelerateDispatch<unknown>, _getState: () => StateTree) => {
        const vppBffUrl = buildFromConfig(bffEndpointConfig);
        const endpoint = `${vppBffUrl}/fetchVppRates/${rateToken}?limitToOnePlan=false`;

        let abortController = fetchRatesAbortControllerMap.get(scopeId);
        if (abortController) {
            abortController.abort();
        }
        abortController = new AbortController();
        fetchRatesAbortControllerMap.set(scopeId, abortController);

        const request: RSAACallRequest = {
            endpoint,
            method: 'GET',
            headers: { 'Content-Type': 'application/json' },
            options: {
                signal: abortController.signal
            },
            types: [
                FETCH_RATES_REQUESTED,
                { type: FETCH_RATES_SUCCESS, meta: { rateToken, ...meta } },
                { type: FETCH_RATES_FAILURE, meta }
            ]
        };
        dispatch({
            [RSAA]: request
        } as RSAACallAction);
    };

export const repeatFetchVehicleProtectionRates = (action: any, bffEndpointConfig: IConfigObj, scopeId?: string) => {
    const { rateToken, retryNumber = 1 } = action.meta;
    const delay = FETCH_RATES_POLLING_DELAY_FACTORS[retryNumber - 1] * 1000;

    return (dispatch: AccelerateDispatch<any, any>) => {
        setTimeout(() => {
            dispatch(
                fetchVehicleProtectionRates(rateToken, bffEndpointConfig, scopeId, {
                    retryNumber: retryNumber + 1
                })
            );
        }, delay);
    };
};

export const retryFetchVehicleProtectionRates = () => (dispatch: AccelerateDispatch<any, any>, getState: any) => {
    const state = getState();
    const rateToken = vehicleProtectionSelectors.getVppRateToken(state);
    const isRatingRetried = vehicleProtectionSelectors.isVppRatingRetried(state);

    const action = {
        type: isRatingRetried ? FETCH_PRODUCTS_RETRY : FETCH_RATES_RETRY,
        ...(!isRatingRetried && { payload: { rateToken } })
    };

    return dispatch(action);
};

export const updatePayment = (includeTaxes?: boolean, productCodes?: string[]): PaymentUpdateAction => {
    return {
        type: PRODUCT_CHANGES_PAYMENT_CALL,
        payload: {
            includeTaxes: includeTaxes === undefined ? true : includeTaxes
        },
        meta: {
            middleware: {
                payment: true,
                productCodes
            }
        }
    };
};

export const updateVppProductMonthlyPayment = (productCode: string, productMonthlyPayment: number) => {
    return {
        type: PRODUCT_MONTHLY_PAYMENT_UPDATED,
        payload: {
            productCode,
            productMonthlyPayment
        }
    };
};

export const updateVppProductTaxed = (productCode: string, isTaxed: boolean) => {
    return {
        type: PRODUCT_TAXED_UPDATED,
        payload: {
            productCode,
            isTaxed
        }
    };
};

export const updateGapProductPrice = (productCode: string, productPrice: number) => {
    return {
        type: GAP_PRICE_UPDATED_BY_LENDER,
        payload: {
            productCode,
            productPrice
        }
    };
};

export const gapPriceAcknowledged = () => {
    return {
        type: GAP_PRICE_ACKNOWLEDGED
    };
};

export const resetVppProductsMonthlyPayment = () => {
    return {
        type: ALL_PRODUCTS_MONTHLY_PAYMENT_RESET
    };
};

export const updatePrevOfferPayloadAfterVppRates =
    (originalAction: AnyFSA | undefined) => (dispatch: (action: any) => void, getState: () => any) => {
        const state = getState();
        const isFetchProductsSuccess = originalAction?.type === FETCH_PRODUCTS_SUCCESS;
        const isNotPaymentReceived = originalAction?.meta?.originalAction?.type !== PAYMENT_RECEIVED;
        if (isFetchProductsSuccess && isNotPaymentReceived) {
            const dealXgId = getDealXgId(state);
            dispatch(updatePrevOfferAfterInit(dealXgId));
        }
    };
